import * as React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusPill,
  cn,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import { ArrowLeft, CalendarRange, Clock, Printer, RefreshCw, Sparkles, User } from "lucide-react";
import type { SchedulingWeekday, TimetableSlotView, TimetableStatusValue } from "@eduviaos/shared";
import { useTimetableDetail } from "@/hooks/useTimetable";

/**
 * Phase G3 FE-4 — standalone per-teacher week schedule.
 *
 * The IA already lands on a timetable's full grid (every section,
 * every period — see `TimetableDetailPage`). FE-3 added a
 * teacher-filter chip bar there so a coordinator can dim every cell
 * not assigned to one teacher. FE-4 extends that pattern with a
 * dedicated, printable, deep-linkable surface so:
 *
 *   - the chip can hand off a URL the head-teacher pastes into a
 *     parent group, and
 *   - the page can be printed as the teacher's "this week" sheet
 *     without dragging along every other section.
 *
 * Route: ``/timetable/:timetableId/teachers/:teacherId``.
 *
 * The page is read-only by design — manual overrides still go
 * through the slot sheet on the IA detail page (which knows about
 * the publish / archive lifecycle). FE-4 does not duplicate those
 * affordances; instead the header carries an "Open in editor"
 * CTA back to the source timetable.
 *
 * Print: the page sets a small ``@media print`` block on the root
 * so the browser print dialog produces a clean A4-friendly page —
 * no nav chrome, no aside, just the grid and the teacher header.
 */
export function TeacherSchedulePage() {
  const params = useParams<{ timetableId: string; teacherId: string }>();
  const timetableId = params.timetableId ?? "";
  const teacherId = params.teacherId ?? "";

  const detail = useTimetableDetail(timetableId);

  if (!timetableId || !teacherId) {
    return <Navigate to="/timetable" replace />;
  }

  if (detail.isLoading) {
    return <PageSkeleton />;
  }

  if (detail.isError || !detail.data) {
    return <PageError onRetry={() => void detail.refetch()} />;
  }

  const { timetable, slots } = detail.data;
  const teacherSlots = slots.filter((s) => s.teacher_id === teacherId);
  const stats = computeTeacherStats(teacherSlots);
  const grid = buildWeeklyGrid(teacherSlots);
  const knownSubjects = Array.from(new Set(teacherSlots.map((s) => s.subject_code))).sort();
  const knownSections = Array.from(
    new Set(teacherSlots.map((s) => `${s.section_grade}-${s.section_id}`)),
  ).sort();

  return (
    <div className="space-y-6 print:space-y-3" data-testid="teacher-schedule-page">
      <PageHeader className="print:hidden">
        <PageHeaderTop>
          <PageHeaderTitle>
            <Button variant="ghost" size="sm" asChild className="self-start">
              <Link to={`/timetable/${encodeURIComponent(timetableId)}`}>
                <ArrowLeft aria-hidden="true" className="size-4" />
                Back to timetable
              </Link>
            </Button>
            <PageHeaderHeading className="flex items-center gap-3">
              <User aria-hidden="true" className="size-5 opacity-70" />
              <span className="font-mono text-base sm:text-xl">{teacherId}</span>
              <StatusPill
                status={STATUS_PILL[timetable.status]}
                label={STATUS_LABEL[timetable.status]}
              />
            </PageHeaderHeading>
            <PageHeaderDescription>
              Weekly schedule on{" "}
              <Link
                to={`/timetable/${encodeURIComponent(timetableId)}`}
                className="text-foreground underline-offset-2 hover:underline"
              >
                {timetable.name}
              </Link>
              {timetable.effective_to ? (
                <>
                  {" "}
                  · valid {timetable.effective_from} → {timetable.effective_to}
                </>
              ) : (
                <> · effective from {timetable.effective_from}</>
              )}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void detail.refetch()}
              disabled={detail.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", detail.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer aria-hidden="true" className="size-4" />
              Print
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {/* Print-only header — minimal, paper-friendly. */}
      <div className="hidden print:block">
        <h1 className="text-lg font-semibold">{teacherId}</h1>
        <p className="text-xs text-neutral-700">
          {timetable.name}
          {timetable.effective_to
            ? ` · ${timetable.effective_from} → ${timetable.effective_to}`
            : ` · from ${timetable.effective_from}`}
        </p>
      </div>

      {teacherSlots.length === 0 ? (
        <EmptyTeacherState
          teacherId={teacherId}
          timetableName={timetable.name}
          backHref={`/timetable/${encodeURIComponent(timetableId)}`}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px] print:grid-cols-1">
          <WeeklyGrid grid={grid} />
          <SummaryAside
            stats={stats}
            subjects={knownSubjects}
            sections={knownSections}
            timetableName={timetable.name}
          />
        </div>
      )}
    </div>
  );
}

// ── Status mapping ───────────────────────────────────────────────────

const STATUS_PILL: Record<TimetableStatusValue, StatusPillStatus> = {
  draft: "inactive",
  published: "active",
  archived: "archived",
};

const STATUS_LABEL: Record<TimetableStatusValue, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

// ── Weekly grid (subset of TimetableDetailPage's grid) ───────────────

const WEEKDAY_ORDER: SchedulingWeekday[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const WEEKDAY_LABEL: Record<SchedulingWeekday, string> = {
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
  SUN: "Sun",
};

interface BuiltGrid {
  weekdays: SchedulingWeekday[];
  periodIndices: number[];
  cells: Map<string, TimetableSlotView>;
  periodLabels: Map<number, { startMin: number; endMin: number }>;
}

function buildWeeklyGrid(slots: TimetableSlotView[]): BuiltGrid {
  const weekdaySet = new Set<SchedulingWeekday>();
  const periodSet = new Set<number>();
  const cells = new Map<string, TimetableSlotView>();
  const periodLabels = new Map<number, { startMin: number; endMin: number }>();
  for (const slot of slots) {
    weekdaySet.add(slot.weekday);
    periodSet.add(slot.period_index);
    cells.set(`${slot.weekday}:${slot.period_index}`, slot);
    if (!periodLabels.has(slot.period_index)) {
      periodLabels.set(slot.period_index, {
        startMin: slot.start_minute,
        endMin: slot.end_minute,
      });
    }
  }
  const weekdays = WEEKDAY_ORDER.filter((d) => weekdaySet.has(d));
  const periodIndices = Array.from(periodSet).sort((a, b) => a - b);
  return { weekdays, periodIndices, cells, periodLabels };
}

interface WeeklyGridProps {
  grid: BuiltGrid;
}

function WeeklyGrid({ grid }: WeeklyGridProps) {
  if (grid.weekdays.length === 0 || grid.periodIndices.length === 0) {
    return null;
  }
  const templateColumns = `120px repeat(${grid.weekdays.length}, minmax(140px, 1fr))`;
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b print:border-b-0 print:py-2">
        <div>
          <CardTitle className="text-base">Week at a glance</CardTitle>
          <p className="text-muted-foreground mt-0.5 text-xs print:hidden">
            Read-only — make corrections back on the timetable editor.
          </p>
        </div>
        <Badge variant="outline" className="font-normal print:hidden">
          {grid.weekdays.length} day{grid.weekdays.length === 1 ? "" : "s"} ·{" "}
          {grid.periodIndices.length} period{grid.periodIndices.length === 1 ? "" : "s"}
        </Badge>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto print:overflow-visible">
          <div
            role="grid"
            aria-label="Teacher weekly schedule"
            data-testid="teacher-week-grid"
            className="divide-border/60 grid divide-y"
            style={{
              gridTemplateColumns: templateColumns,
              minWidth: `${120 + grid.weekdays.length * 140}px`,
            }}
          >
            <div role="columnheader" className="bg-muted/40 px-3 py-2 text-xs font-medium" />
            {grid.weekdays.map((weekday) => (
              <div
                key={weekday}
                role="columnheader"
                className="bg-muted/40 border-border/60 border-l px-3 py-2 text-xs font-medium"
              >
                {WEEKDAY_LABEL[weekday]}
              </div>
            ))}
            {grid.periodIndices.map((periodIdx) => {
              const label = grid.periodLabels.get(periodIdx);
              return (
                <React.Fragment key={periodIdx}>
                  <div role="rowheader" className="bg-muted/20 border-border/60 border-t px-3 py-3">
                    <div className="text-foreground text-xs font-semibold">
                      Period {periodIdx + 1}
                    </div>
                    {label ? (
                      <div className="text-muted-foreground mt-0.5 text-[10px] tabular-nums">
                        {formatMinute(label.startMin)} – {formatMinute(label.endMin)}
                      </div>
                    ) : null}
                  </div>
                  {grid.weekdays.map((weekday) => {
                    const slot = grid.cells.get(`${weekday}:${periodIdx}`);
                    return (
                      <SlotCell key={`${weekday}:${periodIdx}`} slot={slot} weekday={weekday} />
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface SlotCellProps {
  slot: TimetableSlotView | undefined;
  weekday: SchedulingWeekday;
}

function SlotCell({ slot, weekday }: SlotCellProps) {
  if (!slot) {
    return (
      <div
        role="gridcell"
        aria-label={`Free period on ${WEEKDAY_LABEL[weekday]}`}
        className="text-muted-foreground/40 border-border/60 border-l border-t px-3 py-3 text-center text-[10px]"
        data-testid="teacher-slot-empty"
      >
        Free
      </div>
    );
  }
  return (
    <div
      role="gridcell"
      aria-label={`${slot.subject_code} for ${slot.section_grade}-${slot.section_id} on ${WEEKDAY_LABEL[slot.weekday]} period ${slot.period_index + 1}`}
      data-testid="teacher-slot-cell"
      className={cn(
        "border-border/60 border-l border-t px-3 py-3 text-left",
        slot.is_lab_period && "bg-amber-500/5",
        slot.manual_override && "bg-sky-500/5",
      )}
    >
      <div className="text-foreground truncate text-xs font-semibold">{slot.subject_code}</div>
      <div className="text-muted-foreground mt-0.5 truncate text-[10px]">
        {slot.section_grade} · {slot.section_id}
      </div>
      {slot.room_id ? (
        <div className="text-muted-foreground/70 mt-1 truncate text-[10px]">Rm. {slot.room_id}</div>
      ) : null}
      <div className="mt-1 flex flex-wrap gap-1">
        {slot.is_lab_period ? (
          <Badge
            variant="outline"
            className="border-amber-500/40 text-[9px] text-amber-700 dark:text-amber-400"
          >
            Lab
          </Badge>
        ) : null}
        {slot.manual_override ? (
          <Badge
            variant="outline"
            className="border-sky-500/40 text-[9px] text-sky-700 dark:text-sky-400"
          >
            Override
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

// ── Summary aside ────────────────────────────────────────────────────

interface TeacherStats {
  totalSlots: number;
  totalMinutes: number;
  perDay: Record<SchedulingWeekday, number>;
}

function computeTeacherStats(slots: TimetableSlotView[]): TeacherStats {
  const stats: TeacherStats = {
    totalSlots: 0,
    totalMinutes: 0,
    perDay: { MON: 0, TUE: 0, WED: 0, THU: 0, FRI: 0, SAT: 0, SUN: 0 },
  };
  for (const slot of slots) {
    stats.totalSlots += 1;
    stats.totalMinutes += Math.max(0, slot.end_minute - slot.start_minute);
    stats.perDay[slot.weekday] += 1;
  }
  return stats;
}

interface SummaryAsideProps {
  stats: TeacherStats;
  subjects: string[];
  sections: string[];
  timetableName: string;
}

function SummaryAside({ stats, subjects, sections, timetableName }: SummaryAsideProps) {
  return (
    <Card className="self-start print:hidden">
      <CardHeader>
        <CardTitle className="text-base">Workload</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SummaryRow label="Teaching slots" value={stats.totalSlots.toString()} />
        <SummaryRow
          label="Weekly hours"
          value={(stats.totalMinutes / 60).toFixed(1)}
          icon={<Clock className="size-3" aria-hidden="true" />}
        />
        <div className="border-border/60 border-t pt-3">
          <p className="text-muted-foreground mb-2 text-[10px] uppercase tracking-wide">Per day</p>
          <div
            className="text-muted-foreground grid grid-cols-7 gap-1 text-center text-[9px] tabular-nums"
            data-testid="teacher-perday-strip"
          >
            {WEEKDAY_ORDER.map((d) => (
              <div key={d}>
                <div className="text-[8px] uppercase">{WEEKDAY_LABEL[d].slice(0, 1)}</div>
                <div className="text-foreground mt-0.5 font-medium">{stats.perDay[d]}</div>
              </div>
            ))}
          </div>
        </div>
        {subjects.length > 0 ? (
          <div className="border-border/60 space-y-1.5 border-t pt-3">
            <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Subjects</p>
            <div className="flex flex-wrap gap-1.5">
              {subjects.map((code) => (
                <Badge key={code} variant="outline" className="font-mono text-[10px]">
                  {code}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
        {sections.length > 0 ? (
          <div className="border-border/60 space-y-1.5 border-t pt-3">
            <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Sections</p>
            <div className="flex flex-wrap gap-1.5">
              {sections.map((label) => (
                <Badge key={label} variant="outline" className="font-mono text-[10px]">
                  {label}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
        <div className="border-border/60 text-muted-foreground border-t pt-3 text-[11px]">
          <CalendarRange aria-hidden="true" className="mr-1.5 inline size-3" />
          Sourced from “{timetableName}”. Edits land back on the timetable editor.
        </div>
      </CardContent>
    </Card>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

function SummaryRow({ label, value, icon }: SummaryRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
        {icon}
        {label}
      </span>
      <span className="text-foreground text-sm font-medium tabular-nums">{value}</span>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return `${hh}:${mm}`;
}

// ── Skeleton / error / empty states ──────────────────────────────────

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="bg-muted/40 h-24 animate-pulse rounded-md" />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="bg-muted/40 h-[480px] animate-pulse rounded-md" />
        <div className="bg-muted/40 h-[320px] animate-pulse rounded-md" />
      </div>
    </div>
  );
}

function PageError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-3 p-10 text-center">
        <Sparkles aria-hidden="true" className="text-muted-foreground size-6" />
        <div>
          <p className="text-foreground font-medium">Could not load this teacher's schedule</p>
          <p className="text-muted-foreground mt-1 text-sm">
            The timetable might have been archived or the teacher ID is unknown.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </CardContent>
    </Card>
  );
}

interface EmptyTeacherStateProps {
  teacherId: string;
  timetableName: string;
  backHref: string;
}

function EmptyTeacherState({ teacherId, timetableName, backHref }: EmptyTeacherStateProps) {
  return (
    <Card>
      <CardContent
        className="flex flex-col items-center justify-center gap-3 p-10 text-center"
        data-testid="teacher-schedule-empty"
      >
        <Sparkles aria-hidden="true" className="text-muted-foreground size-6" />
        <div>
          <p className="text-foreground font-medium">
            No slots assigned to <span className="font-mono">{teacherId}</span>
          </p>
          <p className="text-muted-foreground mt-1 text-sm">
            “{timetableName}” doesn't include this teacher. They might be on another timetable, or
            the ID was mistyped.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={backHref}>Back to the timetable</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
